import type { SpotDistanceSegment, Terrain } from '@firesim/simkit-core';
import { getTerrainWithOverrides } from '@firesim/simkit-core';

export enum TerrainCodes {
    Empty = 0,
    Road = 1,
    House = 2,
    PukimaniPoles = 3,
    Water = 4,
    Grassland = 5,
    GrassyDirt = 6,
    Scrub = 7,
    Trees = 8,
    VineForest = 9,
    Break = 10
};
// Add a type-safe way to retrieve enum keys or values:
export type TerrainCodeKey = keyof typeof TerrainCodes;
//export type TerrainCodeValue = typeof TerrainCodes[TerrainCodeKey];
export type TerrainCodeValue = number;

export const terrainCodeValues: TerrainCodes[] = Object.values(TerrainCodes).filter(
  (value) => typeof value === 'number'
) as TerrainCodes[];


const spotDistanceTemplates: Record<string, Array<SpotDistanceSegment>> = {
    grass: [{
        weight: 0.70,
        minDistance: 0.5,
        maxDistance: 3
    },
    {
        weight: 0.20,
        minDistance: 0.5,
        maxDistance: 4
    }],
    scrub: [{
        weight: 0.011,
        minDistance: 1,
        maxDistance: 4
    },
    {
        weight: 0.011,
        minDistance: 0,
        maxDistance: 10
    }],
    canopy: [{
        weight: 0.011,
        minDistance: 1,
        maxDistance: 4
    },
    {
        weight: 0.011,
        minDistance: 5,
        maxDistance: 250
    }]
};

export const baseTerrainTypes: Record<TerrainCodes, Terrain<TerrainCodes>> = {
  [TerrainCodes.Empty]: {
    code: TerrainCodes.Empty,
    colour: {
      red: 255,
      green: 255,
      blue: 255,
    },
    fuelLoad: 0
  },
  [TerrainCodes.Break]: {
    code: TerrainCodes.Empty,
    colour: {
      red: 155,
      green: 116,
      blue: 82,
    },
    fuelLoad: 0
  },
    [TerrainCodes.Road]: {
    code: TerrainCodes.Road,
    colour: {
      red: 128,
      green: 128,
      blue: 128,
    },
    fuelLoad: 0
  },
    [TerrainCodes.House]: {
    code: TerrainCodes.House,
    colour: {
      red: 92,
      green: 65,
      blue: 50,
    },
    fuelLoad: 0.85
  },
    [TerrainCodes.PukimaniPoles]: {
    code: TerrainCodes.House,
    colour: {
      red: 92,
      green: 65,
      blue: 50,
    },
    fuelLoad: 0.85
  },
[TerrainCodes.Water]: {
    code: TerrainCodes.Water,
    colour: {
      red: 0,
      green: 105,
      blue: 148,
    },
    fuelLoad: 0
  },
  [TerrainCodes.Grassland]: {
    code: TerrainCodes.Grassland,
    colour: {
      red: 214,
      green: 198,
      blue: 128,
    },
    fuelLoad: 0.98,
    spotDistanceDistribution: spotDistanceTemplates.grass
  },
  [TerrainCodes.GrassyDirt]: {
    code: TerrainCodes.GrassyDirt,
    colour: {
      red: 211,
      green: 180,
      blue: 129,
    },
    fuelLoad: 0.65,
    spotDistanceDistribution: spotDistanceTemplates.grass
  },
  [TerrainCodes.Scrub]: {
    code: TerrainCodes.Scrub,
    colour: {
      red: 85,
      green: 107,
      blue: 47,
    },
    fuelLoad: 0.8,
    fuelLoadFuzzRange: 0.1,
    spotDistanceDistribution: spotDistanceTemplates.scrub
  },
  [TerrainCodes.Trees]: {
    code: TerrainCodes.Trees,
    colour: {
      red: 34,
      green: 139,
      blue: 34,
    },
    fuelLoad: 0.95,
    spotDistanceDistribution: spotDistanceTemplates.canopy
  },
  [TerrainCodes.VineForest]: {
    code: TerrainCodes.VineForest,
    colour: {
      red: 34,
      green: 139,
      blue: 34,
    },
    fuelLoad: 0.7
  }
};

export const terrainTypes = getTerrainWithOverrides(baseTerrainTypes);

export const terrain: (typeof terrainTypes)[TerrainCodes][] = Object.values(terrainTypes);
